import { z } from 'zod';
import { AuditSchema, IdSchema, VisibilitySchema } from './common.ts';
import { RecordRefSchema, type RecordRef } from './notification.ts';

/**
 * How far a file has got through processing.
 *
 * Three states, and the middle one is the point: a file is not usable the
 * moment its bytes land. `processing` means uploaded but not yet checked;
 * `ready` means checked and safe to hand out; `blocked` means it failed and
 * must not be served. A view that treats a missing status as usable would
 * hand out exactly the files this exists to withhold.
 */
export const FILE_STATUSES = ['processing', 'ready', 'blocked'] as const;
export const FileStatusSchema = z.enum(FILE_STATUSES);
export type FileStatus = z.infer<typeof FileStatusSchema>;

/**
 * Why a file was blocked, in words somebody can act on.
 *
 * A fixed list because "blocked" with no reason is a support call. Each one
 * tells the uploader what to do differently.
 */
export const BLOCK_REASONS = ['tooLarge', 'unsupportedType', 'scanFailed'] as const;
export const BlockReasonSchema = z.enum(BLOCK_REASONS);
export type BlockReason = z.infer<typeof BlockReasonSchema>;

/**
 * The three roots a job's files live under.
 *
 * Fixed rather than user-defined, because every job has the same three and
 * naming them per job would make a cross-job query impossible. Folders
 * nest inside them.
 */
export const FOLDER_KINDS = ['documents', 'photos', 'videos'] as const;
export const FolderKindSchema = z.enum(FOLDER_KINDS);
export type FolderKind = z.infer<typeof FolderKindSchema>;

export const FolderSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  name: z.string().trim().min(1).max(120),
  /** Absent for a folder sitting directly under its root. */
  parentId: IdSchema.optional(),
  kind: FolderKindSchema,
  visibility: VisibilitySchema,
  /**
   * Created by the product rather than by a person.
   *
   * "From Owner" and "From Subs" exist so an external upload has somewhere
   * to go that nobody has to create first. They cannot be renamed or
   * deleted: a sub's upload landing in a folder somebody removed last week
   * is a file nobody finds again.
   */
  system: z.boolean().default(false),
});
export type Folder = z.infer<typeof FolderSchema>;

/**
 * One uploaded file.
 *
 * Company-scoped with an optional `jobId`, per `docs/DATA_MODEL.md` §7:
 * company templates and job documents are the same kind of thing and want
 * one collection to query by tag.
 *
 * `storagePath` is where the bytes are and is **not** a URL. A URL expires,
 * and storing one would mean every file document going stale on a schedule
 * nobody controls.
 */
export const FileSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  /** Renameable, and separate from the storage path, which never changes. */
  name: z.string().trim().min(1).max(260),
  storagePath: z.string().min(1),
  size: z.number().int().nonnegative(),
  contentType: z.string().min(1).max(200),
  /** Absent for a company-level file: a template, a form, a logo. */
  jobId: IdSchema.optional(),
  folderId: IdSchema.optional(),
  uploadedBy: IdSchema,
  status: FileStatusSchema,
  blockedReason: BlockReasonSchema.optional(),
  /** Set once a preview has been generated, which not every type gets. */
  thumbPath: z.string().min(1).optional(),
  visibility: VisibilitySchema,
  tagIds: z.array(IdSchema).default([]),
  /**
   * The records this file is attached to.
   *
   * References, both ways deliberately: an attachment is a pointer, so
   * deleting a daily log never deletes the photographs taken that day.
   * `docs/features/11-files-messaging.md` §1 is explicit about it.
   */
  linkedRecords: z.array(RecordRefSchema).default([]),
  /**
   * The same links as `linkedRecords`, flattened to `"<kind>:<id>"`.
   *
   * A query field, and the only reason it exists. Firestore's
   * `array-contains` on an array of maps needs the whole map to match
   * exactly, including optional keys, so "the files attached to this log"
   * would depend on whether `jobId` happened to be set when the link was
   * written — a query that works for some rows and silently misses others.
   *
   * It cannot drift from `linkedRecords` because it is never written
   * separately: `linkKey` derives it, and every write that touches the
   * links recomputes both in the same update.
   */
  linkedKeys: z.array(z.string().min(1)).default([]),
});
export type File = z.infer<typeof FileSchema>;

/**
 * The largest file this product accepts.
 *
 * The feature doc asks for a warning at 100 MB; this is the refusal, set
 * well above it so the warning has room to be a warning. A hard cap exists
 * because an unbounded upload is somebody else's storage bill.
 */
export const MAX_FILE_BYTES = 500 * 1024 * 1024;

/** Where the feature doc asks for a warning rather than a refusal. */
export const LARGE_FILE_BYTES = 100 * 1024 * 1024;

/**
 * Content types this product will serve.
 *
 * An allowlist, not a blocklist. A blocklist is a list of the attacks
 * somebody thought of; everything not named here is refused, which is the
 * only direction that stays correct as formats appear.
 *
 * Deliberately no `text/html` and no `image/svg+xml`: both execute script in
 * the browser that opens them, and these files are served to subs and
 * homeowners from a domain they trust.
 */
export const ALLOWED_CONTENT_TYPES: readonly string[] = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'video/mp4',
  'video/quicktime',
  'application/zip',
];

/**
 * Why a file cannot be served, or null if it can (P3-04 / P3-05f).
 *
 * **In core rather than in the Cloud Function**, and that move is the
 * point. A file lands as `processing` because a client is not allowed to
 * say its own bytes are checked, and the thing that checks them is a
 * Firestore trigger — which `pnpm dev` and the Playwright suite do not
 * have. So every uploaded file sat on "Checking" forever on the default
 * dev path, which was *honest* (nothing had checked it) and still made the
 * product look broken.
 *
 * The in-memory adapter now stands in for that trigger, and the only way
 * for a stand-in to be worth having is for it to apply **this** function
 * rather than its own idea of acceptable. Two implementations of "may this
 * be served" would eventually part company, and the day they did, a file
 * would be blocked on one and served on the other.
 *
 * Re-checked here even though `uploadFile` checked before uploading and
 * the storage rules checked before storing. Three checks, because only
 * this one is both server-side and *after* the bytes exist: the first is a
 * courtesy to the uploader, the second cannot read Firestore, and this one
 * sees what was actually written.
 *
 * `scanFailed` is in `BLOCK_REASONS` and nothing produces it. There is no
 * virus scan — that needs a scanner, which is a dependency decision raised
 * as P3-04d rather than faked. So `ready` means "size and type are
 * acceptable", on the server and on the dev path alike; a stand-in that
 * claimed more would be the exact lie the status exists to prevent.
 *
 * Returns `BlockReason` from `BLOCK_REASONS` above. The functions package
 * had been carrying its **own** `'tooLarge' | 'unsupportedType' |
 * 'scanFailed'` union beside that enum — two hand-maintained lists of the
 * same three strings, which is how the badge and the stored value drift.
 */
export function blockReasonFor(file: {
  size?: unknown;
  contentType?: unknown;
}): BlockReason | null {
  const size = typeof file.size === 'number' ? file.size : Number.NaN;
  // A size that is not a number at all is refused rather than waved
  // through: a document that cannot be measured cannot be quota'd either.
  if (!Number.isFinite(size) || size > MAX_FILE_BYTES) return 'tooLarge';
  const contentType = typeof file.contentType === 'string' ? file.contentType : '';
  if (!ALLOWED_CONTENT_TYPES.includes(contentType)) return 'unsupportedType';
  return null;
}

/** Which root a type belongs in, so an upload files itself. */
export function folderKindFor(contentType: string): FolderKind {
  if (contentType.startsWith('image/')) return 'photos';
  if (contentType.startsWith('video/')) return 'videos';
  return 'documents';
}

/** Whether a file may be handed to anybody yet. */
export function isServable(file: Pick<File, 'status'>): boolean {
  return file.status === 'ready';
}

/** The query form of a link. Derived, never typed by hand. */
export function linkKey(ref: { readonly kind: string; readonly id: string }): string {
  return `${ref.kind}:${ref.id}`;
}

/** Both link fields for a set of records, so the two cannot be written apart. */
export function linkFields(
  records: readonly RecordRef[],
): Pick<File, 'linkedRecords' | 'linkedKeys'> {
  return { linkedRecords: [...records], linkedKeys: records.map(linkKey) };
}
